import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { InlineSpinner } from "@/components/common/Loading";
import { MemberCombobox } from "./MemberCombobox";
import { AlertTriangle, History, LifeBuoy, Settings2, RotateCcw } from "lucide-react";

function AreaRow({ area, members, canManage, onSave }) {
  const a = area.assignment;
  const [pending, setPending] = useState(undefined);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const value = pending !== undefined ? pending : (a?.member_id || null);
  const fit = members.filter((m) => !m.allowed_areas || m.allowed_areas.includes(area.key));
  const options = a && !a.eligible ? [{ member_id: a.member_id, name: `${a.name} (unavailable)`, email: a.email }, ...fit] : fit;

  const save = async (mid) => {
    setPending(mid); setError(null); setBusy(true);
    try { await onSave(area.key, mid); setPending(undefined); toast.success(mid ? `${area.label} support saved` : `${area.label} support cleared`); }
    catch (e) { setError(apiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="py-3 grid gap-2 md:grid-cols-[minmax(0,1.1fr)_minmax(0,1fr)] md:items-center" data-testid={`support-area-${area.key}`}>
      <div className="min-w-0">
        <p className="text-sm font-semibold text-slate-800">{area.label}</p>
        <p className="text-xs text-slate-500">{area.focus}</p>
      </div>
      <div className="min-w-0">
        {canManage ? (
          <MemberCombobox members={options} value={value} onChange={save} disabled={busy} testId={`support-select-${area.key}`} />
        ) : (
          <p className={`text-sm ${a ? "text-slate-800" : "text-slate-400"}`} data-testid={`support-current-${area.key}`}>{a ? a.name : "Not assigned"}</p>
        )}
        {a?.email && !error && <p className="text-[11px] text-slate-400 mt-0.5 truncate" data-testid={`support-email-${area.key}`}>{a.email}</p>}
        {a?.needs_reassignment && (
          <p className="text-xs text-amber-700 mt-1 flex items-start gap-1" data-testid={`support-flag-${area.key}`}>
            <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />Needs reassignment: {a.issue}</p>
        )}
        {a?.eligible && a.access_warning && canManage && (
          <p className="text-xs text-amber-700 mt-1" data-testid={`support-access-${area.key}`}>{a.access_warning}</p>
        )}
        {error && (
          <p className="text-xs text-rose-600 mt-1 flex items-center gap-2" role="alert" data-testid={`support-error-${area.key}`}>
            Couldn't save: {error}
            <button onClick={() => save(value)} className="underline inline-flex items-center gap-0.5" data-testid={`support-retry-${area.key}`}><RotateCcw className="w-3 h-3" />Retry</button>
          </p>
        )}
      </div>
    </div>
  );
}

export function SupportAssignmentsCard({ personId }) {
  const [data, setData] = useState(null);
  const [members, setMembers] = useState([]);
  const [showHistory, setShowHistory] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data: d } = await api.get(`/support-assignments/person/${personId}`);
      setData(d);
      if (d.can_manage && d.organization_id) {
        const r = await api.get("/support-assignments/eligible", { params: { organization_id: d.organization_id } });
        setMembers(r.data.items || []);
      }
    } catch (e) { setData({ error: apiError(e) }); }
  }, [personId]);
  useEffect(() => { load(); }, [load]);

  const onSave = async (area, memberId) => {
    const { data: d } = await api.put(`/support-assignments/person/${personId}/${area}`, { member_id: memberId });
    setData(d);
  };

  if (!data) return <div className="rounded-xl border border-slate-200 bg-white p-5 flex justify-center"><InlineSpinner /></div>;
  if (data.error) return (
    <div className="rounded-xl border border-slate-200 bg-white p-5 text-sm text-rose-600 flex items-center gap-3" data-testid="support-load-error">
      {data.error}<button onClick={() => { setData(null); load(); }} className="underline" data-testid="support-load-retry">Retry</button>
    </div>
  );

  return (
    <section className="rounded-xl border border-slate-200 bg-white p-5" data-testid="support-assignments-card">
      <div className="flex flex-wrap items-start justify-between gap-3 mb-2">
        <div className="flex items-start gap-2">
          <span className="w-8 h-8 rounded-lg bg-[#006090]/10 text-[#006090] flex items-center justify-center shrink-0"><LifeBuoy className="w-4 h-4" /></span>
          <div>
            <h3 className="font-display font-semibold text-slate-900">Current Support Assignments</h3>
            <p className="text-xs text-slate-500">Organization Support Team members helping in each area{data.organization_name ? ` · ${data.organization_name}` : ""}</p>
          </div>
        </div>
        {data.can_manage && (
          <Link to={`/admin/support-team?org=${data.organization_id}#org-support-team`} data-testid="manage-support-team-link"
            className="inline-flex items-center gap-1.5 text-sm font-medium text-[#006090] hover:underline">
            <Settings2 className="w-4 h-4" />Manage Support Team
          </Link>
        )}
      </div>
      {!data.supported ? (
        <p className="text-sm text-slate-500 py-3" data-testid="support-not-applicable">Support Assignments are available for Candidates and Agents.</p>
      ) : !data.organization_id ? (
        <p className="text-sm text-slate-500 py-3">Assign this person to an organization to set up support.</p>
      ) : (
        <>
          {data.can_manage && members.length === 0 && (
            <p className="text-xs text-amber-700 bg-amber-50 rounded-md px-3 py-2 mt-2" data-testid="support-roster-empty">
              No one is on this organization's Support Team yet. Use Manage Support Team to add members.</p>
          )}
          <div className="divide-y divide-slate-100">
            {data.areas.map((a) => <AreaRow key={a.key} area={a} members={members} canManage={data.can_manage} onSave={onSave} />)}
          </div>
          {data.can_manage && data.history.length > 0 && (
            <div className="mt-3">
              <button onClick={() => setShowHistory((s) => !s)} data-testid="support-history-toggle"
                className="text-xs font-medium text-slate-500 hover:text-slate-700 inline-flex items-center gap-1"><History className="w-3.5 h-3.5" />
                {showHistory ? "Hide" : "Show"} assignment history ({data.history.length})</button>
              {showHistory && (
                <ul className="mt-2 space-y-1.5 text-xs text-slate-600" data-testid="support-history">
                  {data.history.map((h, i) => (
                    <li key={i}><span className="font-medium">{h.area_label}:</span> {h.member_name} · assigned {new Date(h.assigned_at).toLocaleString()} by {h.assigned_by_name || "—"}
                      {h.ended_at ? ` · ended ${new Date(h.ended_at).toLocaleString()} by ${h.ended_by_name || "—"} (${(h.end_reason || "").replace("_", " ")})` : " · current"}</li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </>
      )}
    </section>
  );
}
